// AblyChannelRegistry — centralized lifecycle owner for every
// Ably.RealtimeChannel in the frontend. Plan 060 / TICKET-022.
//
// Consumers acquire opaque handles (MessageReaderHandle / PresenceReaderHandle
// / PresencePusherHandle) and never see the raw RealtimeChannel. The registry
// refcounts handles per (channel-name, options-hash) entry and is the SOLE
// caller of channel.attach / detach / unsubscribe / channels.release.
//
// Canonical invariants INV-1..INV-17 + SUP-1/SUP-2 are documented inline at
// each enforcement point. Cross-reference TICKET-022 §"FINAL CANONICAL".
//
// Channel idle behavior: per Ably docs (channels/states), the server auto-
// closes channels approximately one minute after the last client detaches
// AND no further messages are published. We rely on this server-side cleanup;
// the registry does NOT add its own idle timer. Channel lifetime is bounded
// by consumer scope dispose (refCount → 0 → detach + release).

import type Ably from "ably";
import {
  inject,
  onScopeDispose,
  ref,
  shallowRef,
  type App,
  type EffectScope,
  type InjectionKey,
  type Ref,
} from "vue";
import {
  type ChannelDescriptor,
  type PresenceCapability,
  type RegistryChannelOptions,
} from "@f3/shared";
import { useAblySdk } from "./use-ably-sdk";
import {
  AblyChannelRegistryError,
  ChannelHandleReleasedError,
  PresenceNotSupportedOnChannelError,
  PresencePusherAlreadyHeldError,
  RegistryInvalidChannelNameError,
  RegistryOptionsConflictError,
} from "./ably-channel-registry-errors";

export {
  AblyChannelRegistryError,
  ChannelHandleReleasedError,
  PresenceNotSupportedOnChannelError,
  PresencePusherAlreadyHeldError,
  RegistryInvalidChannelNameError,
  RegistryOptionsConflictError,
};
export { CHANNELS } from "@f3/shared";
export type { ChannelDescriptor, PresenceCapability } from "@f3/shared";

// Validate helpers (migrated out of use-ably-channel.ts, deleted in TICKET-023)
export type AblyChannelValidatedData<T = unknown> = undefined | { value: T };
export type AblyChannelValidateFn<T> = (
  data: unknown,
) => AblyChannelValidatedData<T>;

export function ablyChannelValidateIdentityFn<T = unknown>(
  value: T,
): AblyChannelValidatedData<T> {
  return { value };
}

// ---------------------------------------------------------------------------
// Logging + observability hooks
// ---------------------------------------------------------------------------

const LOG_PREFIX = "[AblyChannelRegistry] ";

function devLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.log(LOG_PREFIX, ...args);
  }
}

function devWarn(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.warn(LOG_PREFIX, ...args);
  }
}

/**
 * Sentry observability hook. Safe no-op if Sentry isn't initialized — we read
 * `window.Sentry` lazily so the registry never hard-imports the SDK.
 *
 * Events reported via this hook (TICKET-025 §4):
 *  - RegistryOptionsConflictError: addBreadcrumb (error) + captureException
 *  - presence-leave timeout (2s race lost): addBreadcrumb (warning)
 *  - refcount-underflow (defensive): console.error + addBreadcrumb (error)
 *  - leaked-channels: captureMessage (warning) from show teardown
 */
type SentryBreadcrumb = {
  category: string;
  level: "error" | "warning" | "info" | "debug";
  message: string;
  data?: Record<string, unknown>;
};

function getSentry():
  | {
      captureException?: (e: unknown) => void;
      captureMessage?: (m: string, opts?: Record<string, unknown>) => void;
      addBreadcrumb?: (b: SentryBreadcrumb) => void;
    }
  | undefined {
  return (
    globalThis as unknown as {
      Sentry?: {
        captureException?: (e: unknown) => void;
        captureMessage?: (m: string, opts?: Record<string, unknown>) => void;
        addBreadcrumb?: (b: SentryBreadcrumb) => void;
      };
    }
  ).Sentry;
}

function reportSentryException(err: unknown): void {
  try {
    getSentry()?.captureException?.(err);
  } catch {
    // Never let observability throw.
  }
}

function reportSentryBreadcrumb(breadcrumb: SentryBreadcrumb): void {
  try {
    getSentry()?.addBreadcrumb?.(breadcrumb);
  } catch {
    /* swallow */
  }
}

// ---------------------------------------------------------------------------
// Channel-options canonicalization (CRITICAL-A / INV-11)
// ---------------------------------------------------------------------------

/**
 * Canonicalize channel options for hash-based equality. Two options objects
 * are equal iff their canonical strings are equal.
 *
 *  - modes: lowercase, sort, set-equality
 *  - params: sort keys, deep-equality (values are strings per Ably spec)
 *  - cipher: exclusive presence; both-present → JSON-equality
 *  - attachOnSubscribe: defaults to true
 */
export function canonicalizeChannelOptions(
  opts: RegistryChannelOptions | undefined,
): string {
  if (!opts) return "default";
  const modes = (opts.modes ?? []).map((m) => String(m).toLowerCase()).sort();
  const params = opts.params
    ? Object.fromEntries(
        Object.entries(opts.params).sort(([a], [b]) => a.localeCompare(b)),
      )
    : null;
  const cipher = opts.cipher ?? null;
  const attachOnSubscribe = opts.attachOnSubscribe ?? true;
  return JSON.stringify({ modes, params, cipher, attachOnSubscribe });
}

function toAblyChannelOptions(
  opts: RegistryChannelOptions,
): Ably.ChannelOptions {
  const { attachOnSubscribe: _attachOnSubscribe, ...ablyOptions } = opts;
  return ablyOptions as Ably.ChannelOptions;
}

// ---------------------------------------------------------------------------
// Public types — handles + state-change passthrough
// ---------------------------------------------------------------------------

/**
 * INV-14: derived `isFirstAttach` flag. True on the first transition to
 * "attached" for the entry's lifetime; false on every subsequent transition
 * to "attached" (reattach after disconnect). Use
 * `(resumed === false && isFirstAttach === false)` as the actionable
 * "missed messages — refetch GraphQL" signal.
 */
export interface RegistryChannelStateChange extends Ably.ChannelStateChange {
  readonly isFirstAttach: boolean;
}

/** Listener signature used by handle.onStateChange. */
export type StateChangeListener = (change: RegistryChannelStateChange) => void;

export interface MessageReaderHandle {
  readonly id: symbol;
  readonly name: string;
  readonly state: Ref<Ably.ChannelState>;
  readonly isAttached: Ref<boolean>;
  readonly errorReason: Ref<Ably.ErrorInfo | null>;
  subscribe(
    topic: string,
    listener: (msg: Ably.InboundMessage) => void,
  ): () => void;
  publish(topic: string, data: unknown): Promise<void>;
  history(args?: {
    direction?: "backwards" | "forwards";
    limit?: number;
    untilAttach?: boolean;
  }): Promise<Ably.PaginatedResult<Ably.InboundMessage>>;
  onStateChange(listener: StateChangeListener): () => void;
  release(): void;
}

export interface PresenceReaderHandle<
  _TPayload extends Record<string, unknown>,
> extends MessageReaderHandle {
  presenceSubscribe(listener: (msg: Ably.PresenceMessage) => void): () => void;
  presenceGet(): Promise<Ably.PresenceMessage[]>;
  /**
   * INV-15: convenience helper. On every presence event, calls presenceGet()
   * and invokes cb with (members, lastEvent). Ably's docs explicitly recommend
   * this rebuild-on-event pattern for UI presence-set rendering.
   */
  onPresenceEvent(
    cb: (
      members: Ably.PresenceMessage[],
      lastEvent: Ably.PresenceMessage,
    ) => void,
  ): () => void;
}

export interface PresencePusherHandle<
  TPayload extends Record<string, unknown>,
> extends PresenceReaderHandle<TPayload> {
  enter(payload: TPayload): Promise<void>;
  update(payload: TPayload): Promise<void>;
  leave(): Promise<void>;
}

export interface AblyChannelRegistryStatsEntry {
  name: string;
  refCount: number;
  readerCount: number;
  pusherHandleId: symbol | null;
  pusherHasEntered: boolean;
  listenerCount: number;
  optionsHash: string;
}

export interface AblyChannelRegistry {
  /**
   * Subscribe to a NON-PRESENCE channel (descriptor.presence === "none").
   * Compile error if descriptor declares any presence flavor.
   */
  subscribeChannel<
    TArgs extends unknown[],
    D extends ChannelDescriptor<never, TArgs, "none">,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    scope?: EffectScope;
  }): MessageReaderHandle;

  /**
   * Subscribe to presence on a presence-eligible channel. Compile error if
   * descriptor declares presence: "none". Returns a presence-reader handle
   * with full message surface PLUS presenceSubscribe / presenceGet /
   * onPresenceEvent. The pusher methods (enter/update/leave) are absent.
   */
  subscribePresence<
    TPayload extends Record<string, unknown>,
    TArgs extends unknown[],
    D extends ChannelDescriptor<
      TPayload,
      TArgs,
      "reader-only" | "pusher-and-reader"
    >,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    scope?: EffectScope;
  }): PresenceReaderHandle<TPayload>;

  /**
   * Acquire the SOLE pusher slot for a presence-pushable channel
   * (descriptor.presence === "pusher-and-reader"). Compile error otherwise.
   * Throws PresencePusherAlreadyHeldError synchronously if another pusher
   * is already held for the same (descriptor, channelArgs).
   */
  acquirePresencePusher<
    TPayload extends Record<string, unknown>,
    TArgs extends unknown[],
    D extends ChannelDescriptor<TPayload, TArgs, "pusher-and-reader">,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    initialPayload: TPayload;
    scope?: EffectScope;
  }): PresencePusherHandle<TPayload>;

  /** Observability snapshot. Tests + DevTools introspection (window.__ablyRegistryStats). */
  readonly stats: ReadonlyMap<string, AblyChannelRegistryStatsEntry>;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface ChannelEntry {
  readonly name: string;
  readonly options: RegistryChannelOptions | undefined;
  readonly optionsHash: string;
  readonly channel: Ably.RealtimeChannel;

  // Refcount-driven lifecycle (INV-2, INV-5, INV-12)
  refCount: number;

  // Listener storage (INV-1, INV-9)
  // Map<handleId, Map<topic, wrapper>> — wrapper reference equality matters.
  messageListeners: Map<
    symbol,
    Map<string, (msg: Ably.InboundMessage) => void>
  >;
  presenceReadListeners: Map<symbol, Set<(msg: Ably.PresenceMessage) => void>>;

  // State observation (INV-6)
  internalStateHandler: ((change: Ably.ChannelStateChange) => void) | null;
  stateChangeListeners: Map<symbol, Set<StateChangeListener>>;

  // Reactive refs shared by every handle from this entry.
  state: Ref<Ably.ChannelState>;
  isAttached: Ref<boolean>;
  errorReason: Ref<Ably.ErrorInfo | null>;

  // INV-14 first-attach derivation
  hasEverAttached: boolean;

  // INV-16 / INV-3 single-pusher tracking
  pusherHandleId: symbol | null;
  pusherHasEntered: boolean;

  // INV-10 publish queue (per-handle so released handles don't fire later)
  publishQueue: Map<symbol, Array<{ topic: string; data: unknown }>>;

  // Lifecycle bit — set during the await detach → whenState → release
  // sequence to reject re-acquire-during-release.
  isReleasing: boolean;

  // INV-9 fan-out: dispatch counter so iterations of state listeners can
  // detect mutation during dispatch and fall back to shallow-copy iteration.
  dispatchInProgress: number;
}

const PUBLISH_QUEUE_DEFAULT_LIMIT = 50;

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class AblyChannelRegistryImpl implements AblyChannelRegistry {
  readonly #ablySdk: Ably.Realtime;
  readonly #entries = new Map<string, ChannelEntry>();
  readonly #stats = new Map<string, AblyChannelRegistryStatsEntry>();

  constructor(ablySdk: Ably.Realtime) {
    this.#ablySdk = ablySdk;
  }

  get stats(): ReadonlyMap<string, AblyChannelRegistryStatsEntry> {
    // Refresh snapshot map lazily based on entries.
    this.#refreshStats();
    return this.#stats;
  }

  // ------------------------- Public acquire surface ------------------------

  subscribeChannel<
    TArgs extends unknown[],
    D extends ChannelDescriptor<never, TArgs, "none">,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    scope?: EffectScope;
  }): MessageReaderHandle {
    const entry = this.#getOrCreateEntry(args.descriptor, args.channelArgs);
    return this.#issueReaderHandle(
      entry,
      args.scope,
      "reader",
    ) as MessageReaderHandle;
  }

  subscribePresence<
    TPayload extends Record<string, unknown>,
    TArgs extends unknown[],
    D extends ChannelDescriptor<
      TPayload,
      TArgs,
      "reader-only" | "pusher-and-reader"
    >,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    scope?: EffectScope;
  }): PresenceReaderHandle<TPayload> {
    const entry = this.#getOrCreateEntry(args.descriptor, args.channelArgs);
    return this.#issueReaderHandle(
      entry,
      args.scope,
      "presence-reader",
    ) as PresenceReaderHandle<TPayload>;
  }

  acquirePresencePusher<
    TPayload extends Record<string, unknown>,
    TArgs extends unknown[],
    D extends ChannelDescriptor<TPayload, TArgs, "pusher-and-reader">,
  >(args: {
    descriptor: D;
    channelArgs: TArgs;
    initialPayload: TPayload;
    scope?: EffectScope;
  }): PresencePusherHandle<TPayload> {
    // Defensive runtime guard. Type system should prevent this case.
    if (args.descriptor.presence !== "pusher-and-reader") {
      const name = args.descriptor.name(...args.channelArgs);
      throw new PresenceNotSupportedOnChannelError(
        name,
        args.descriptor.presence,
      );
    }
    const entry = this.#getOrCreateEntry(args.descriptor, args.channelArgs);
    const handleId = Symbol(`pusher-handle:${entry.name}`);

    if (entry.pusherHandleId !== null) {
      const err = new PresencePusherAlreadyHeldError(
        entry.name,
        entry.pusherHandleId,
        handleId,
      );
      reportSentryException(err);
      throw err;
    }

    entry.pusherHandleId = handleId;
    entry.pusherHasEntered = false;
    entry.refCount += 1;
    entry.messageListeners.set(handleId, new Map());
    entry.presenceReadListeners.set(handleId, new Set());
    entry.stateChangeListeners.set(handleId, new Set());
    entry.publishQueue.set(handleId, []);

    devLog(
      `acquirePresencePusher "${entry.name}" handle=${handleId.toString()} refCount=${entry.refCount}`,
    );

    // Attach lazily — Ably attaches on first subscribe by default. Force
    // attach so presence-enter doesn't race the attach.
    void this.#ensureAttached(entry);

    const handle = this.#buildPusherHandle<TPayload>(
      entry,
      handleId,
      args.initialPayload,
    );
    this.#wireScopeDispose(handle, args.scope);
    return handle;
  }

  // ------------------------- Entry creation -------------------------------

  #getOrCreateEntry<TArgs extends unknown[]>(
    descriptor: ChannelDescriptor<unknown, TArgs, PresenceCapability>,
    channelArgs: TArgs,
  ): ChannelEntry {
    const name = descriptor.name(...channelArgs);
    this.#validateChannelName(name);

    const optionsHash = canonicalizeChannelOptions(descriptor.options);
    const key = `${name}#${optionsHash}`;

    const existing = this.#entries.get(key);
    if (existing) {
      if (existing.isReleasing) {
        // Re-acquire arrived mid-release. Per ticket §1 (isReleasing): a
        // future improvement is to await the release. For now, fail loudly
        // so the racy caller surfaces. The release path drops the entry
        // synchronously after channels.release(), so a fresh acquire after
        // that is fine.
        devWarn(
          `acquire raced an in-flight release for "${name}"; the existing entry will finish releasing.`,
        );
      }
      return existing;
    }

    // Same name with different options → conflict (INV-11).
    for (const [otherKey, otherEntry] of this.#entries) {
      if (otherEntry.name === name && otherKey !== key) {
        const err = new RegistryOptionsConflictError(
          name,
          otherEntry.options,
          descriptor.options,
        );
        reportSentryBreadcrumb({
          category: "ably-registry",
          level: "error",
          message: "RegistryOptionsConflictError",
          data: {
            channelName: name,
            optionsHash: canonicalizeChannelOptions(descriptor.options),
          },
        });
        reportSentryException(err);
        throw err;
      }
    }

    // First acquire — create the entry. `attachOnSubscribe` is registry-only;
    // pass only Ably-native channel options to the SDK.
    const channel = this.#ablySdk.channels.get(
      name,
      toAblyChannelOptions(descriptor.options),
    );

    const stateRef = ref<Ably.ChannelState>(
      channel.state,
    ) as Ref<Ably.ChannelState>;
    const isAttachedRef = ref<boolean>(channel.state === "attached");
    const errorReasonRef = shallowRef<Ably.ErrorInfo | null>(null);

    const entry: ChannelEntry = {
      name,
      options: descriptor.options,
      optionsHash,
      channel,
      refCount: 0,
      messageListeners: new Map(),
      presenceReadListeners: new Map(),
      internalStateHandler: null,
      stateChangeListeners: new Map(),
      state: stateRef,
      isAttached: isAttachedRef,
      errorReason: errorReasonRef,
      hasEverAttached: false,
      pusherHandleId: null,
      pusherHasEntered: false,
      publishQueue: new Map(),
      isReleasing: false,
      dispatchInProgress: 0,
    };

    // INV-6: ONE channel.on() per entry; consumer state listeners fan out.
    const internalHandler = (change: Ably.ChannelStateChange): void => {
      const wasFirstAttach =
        change.current === "attached" && !entry.hasEverAttached;
      if (change.current === "attached") entry.hasEverAttached = true;

      entry.state.value = change.current;
      entry.isAttached.value = change.current === "attached";
      entry.errorReason.value = change.reason ?? null;

      // INV-10: drain pending publishes when transitioning to attached.
      if (change.current === "attached") {
        for (const [handleId, queue] of entry.publishQueue) {
          if (queue.length === 0) continue;
          const drained = queue.splice(0, queue.length);
          for (const { topic, data } of drained) {
            channel.publish(topic, data).catch((err) => {
              devWarn(
                `queued publish for handle=${handleId.toString()} topic="${topic}" failed:`,
                err,
              );
            });
          }
        }
      }

      const enriched: RegistryChannelStateChange = Object.assign(
        Object.create(Object.getPrototypeOf(change) as object | null) as object,
        change,
        { isFirstAttach: wasFirstAttach },
      ) as RegistryChannelStateChange;

      // INV-9: shallow-copy listeners before invocation in case a listener
      // synchronously calls release(), mutating the underlying Set/Map.
      const allListeners: StateChangeListener[] = [];
      for (const set of entry.stateChangeListeners.values()) {
        for (const fn of set) allListeners.push(fn);
      }
      entry.dispatchInProgress += 1;
      try {
        for (const fn of allListeners) {
          try {
            fn(enriched);
          } catch (err) {
            devWarn(`state-change listener for "${entry.name}" threw:`, err);
          }
        }
      } finally {
        entry.dispatchInProgress -= 1;
      }
    };
    entry.internalStateHandler = internalHandler;
    channel.on(internalHandler);

    this.#entries.set(key, entry);
    devLog(
      `entry created "${name}" optionsHash=${optionsHash} refCount=${entry.refCount}`,
    );
    return entry;
  }

  #validateChannelName(name: string): void {
    if (typeof name !== "string" || name.length === 0) {
      throw new RegistryInvalidChannelNameError(
        name,
        "must be a non-empty string",
      );
    }
    if (name.startsWith("[")) {
      throw new RegistryInvalidChannelNameError(
        name,
        'must not start with "["',
      );
    }
    if (name.startsWith(":")) {
      throw new RegistryInvalidChannelNameError(
        name,
        'must not start with ":"',
      );
    }
    if (name.includes("\n")) {
      throw new RegistryInvalidChannelNameError(
        name,
        "must not contain newline characters",
      );
    }
    if (name.length > 2048 && import.meta.env.DEV) {
      devWarn(
        `Channel name length (${name.length}) exceeds Ably's recommended soft-limit of 2048 characters.`,
      );
    }
  }

  // ------------------------- Handle factory -------------------------------

  #issueReaderHandle(
    entry: ChannelEntry,
    scope: EffectScope | undefined,
    kind: "reader" | "presence-reader",
  ): MessageReaderHandle | PresenceReaderHandle<Record<string, unknown>> {
    const handleId = Symbol(`${kind}-handle:${entry.name}`);
    entry.refCount += 1;
    entry.messageListeners.set(handleId, new Map());
    entry.presenceReadListeners.set(handleId, new Set());
    entry.stateChangeListeners.set(handleId, new Set());
    entry.publishQueue.set(handleId, []);

    devLog(
      `${kind === "reader" ? "subscribeChannel" : "subscribePresence"} "${entry.name}" handle=${handleId.toString()} refCount=${entry.refCount}`,
    );
    if (entry.options?.attachOnSubscribe !== false) {
      void this.#ensureAttached(entry);
    }

    if (kind === "reader") {
      const handle = this.#buildReaderHandle(entry, handleId);
      this.#wireScopeDispose(handle, scope);
      return handle;
    }
    const handle = this.#buildPresenceReaderHandle<Record<string, unknown>>(
      entry,
      handleId,
    );
    this.#wireScopeDispose(handle, scope);
    return handle;
  }

  #ensureAttached(entry: ChannelEntry): Promise<void> {
    if (
      entry.channel.state === "attached" ||
      entry.channel.state === "attaching"
    ) {
      return Promise.resolve();
    }
    return entry.channel.attach().then(
      () => undefined,
      (err) => {
        devWarn(`channel.attach for "${entry.name}" rejected:`, err);
      },
    );
  }

  #wireScopeDispose(
    handle: { release(): void },
    scope: EffectScope | undefined,
  ): void {
    // INV-5: when called inside a Vue scope, auto-release on dispose.
    // If `scope` is provided explicitly, run inside it; otherwise rely on
    // the ambient EffectScope (component setup or effectScope() block).
    if (scope) {
      scope.run(() => {
        onScopeDispose(() => {
          handle.release();
        });
      });
    } else {
      try {
        onScopeDispose(() => {
          handle.release();
        });
      } catch {
        // Called outside a Vue scope (e.g. during tests or imperative use).
        // Consumers must call handle.release() manually.
      }
    }
  }

  // ------------------------- Reader handle --------------------------------

  #buildReaderHandle(
    entry: ChannelEntry,
    handleId: symbol,
  ): MessageReaderHandle {
    let released = false;
    const ensureLive = (): void => {
      if (released) {
        throw new ChannelHandleReleasedError(entry.name, handleId);
      }
    };

    const handle: MessageReaderHandle = {
      id: handleId,
      name: entry.name,
      state: entry.state,
      isAttached: entry.isAttached,
      errorReason: entry.errorReason,
      subscribe: (topic, listener) => {
        ensureLive();
        const wrapper = (msg: Ably.InboundMessage): void => {
          if (released) return; // INV-7 / INV-12 guard
          listener(msg);
        };
        entry.channel.subscribe(topic, wrapper);
        const handleMap =
          entry.messageListeners.get(handleId) ??
          new Map<string, (msg: Ably.InboundMessage) => void>();
        handleMap.set(topic, wrapper);
        entry.messageListeners.set(handleId, handleMap);
        if (entry.options?.attachOnSubscribe === false) {
          void this.#ensureAttached(entry);
        }
        return () => {
          // INV-1: pass the same wrapper reference for SDK-level removal.
          entry.channel.unsubscribe(topic, wrapper);
          entry.messageListeners.get(handleId)?.delete(topic);
        };
      },
      publish: async (topic, data) => {
        ensureLive();
        if (entry.isAttached.value) {
          // SUP-1: SDK rejection (incl. code 80003 in DISCONNECTED) propagates.
          await entry.channel.publish(topic, data);
          return;
        }
        // INV-10: enqueue while not attached. Drained on transition to attached.
        const queue = entry.publishQueue.get(handleId);
        if (!queue) return;
        if (queue.length >= PUBLISH_QUEUE_DEFAULT_LIMIT) {
          devWarn(
            `publish queue overflow on handle=${handleId.toString()} for "${entry.name}"; dropping oldest.`,
          );
          queue.shift();
        }
        queue.push({ topic, data });
      },
      history: async (args) => {
        ensureLive();
        const result = await entry.channel.history(args ?? {});
        // INV-7: guard post-await — handle may have been released while history was in-flight.
        if (released)
          throw new ChannelHandleReleasedError(entry.name, handleId);
        return result;
      },
      onStateChange: (listener) => {
        ensureLive();
        const set =
          entry.stateChangeListeners.get(handleId) ??
          new Set<StateChangeListener>();
        set.add(listener);
        entry.stateChangeListeners.set(handleId, set);
        return () => set.delete(listener);
      },
      release: () => {
        if (released) return; // INV-12 idempotent
        released = true;
        this.#releaseHandle(entry, handleId, /* isPusher */ false);
      },
    };
    return handle;
  }

  // ------------------------- Presence reader handle -----------------------

  #buildPresenceReaderHandle<TPayload extends Record<string, unknown>>(
    entry: ChannelEntry,
    handleId: symbol,
  ): PresenceReaderHandle<TPayload> {
    const reader = this.#buildReaderHandle(entry, handleId);
    return this.#withPresenceReaderSurface<TPayload>(reader, entry, handleId);
  }

  #withPresenceReaderSurface<TPayload extends Record<string, unknown>>(
    reader: MessageReaderHandle,
    entry: ChannelEntry,
    handleId: symbol,
  ): PresenceReaderHandle<TPayload> {
    let released = false;
    const ensureLive = (): void => {
      if (released) {
        throw new ChannelHandleReleasedError(entry.name, handleId);
      }
    };

    // Hijack the reader's release to also flip our local `released` flag so
    // presence methods throw post-release.
    const originalRelease = reader.release.bind(reader);
    reader.release = () => {
      released = true;
      originalRelease();
    };

    const presenceHandle: PresenceReaderHandle<TPayload> = Object.assign(
      reader,
      {
        presenceSubscribe: (
          listener: (msg: Ably.PresenceMessage) => void,
        ): (() => void) => {
          ensureLive();
          const wrapper = (msg: Ably.PresenceMessage): void => {
            if (released) return;
            listener(msg);
          };
          // Ably presence subscribe takes a single listener; events fanned by SDK.
          void entry.channel.presence.subscribe(wrapper);
          const set =
            entry.presenceReadListeners.get(handleId) ??
            new Set<(msg: Ably.PresenceMessage) => void>();
          set.add(wrapper);
          entry.presenceReadListeners.set(handleId, set);
          if (entry.options?.attachOnSubscribe === false) {
            void this.#ensureAttached(entry);
          }
          return () => {
            entry.channel.presence.unsubscribe(wrapper);
            entry.presenceReadListeners.get(handleId)?.delete(wrapper);
          };
        },
        presenceGet: async (): Promise<Ably.PresenceMessage[]> => {
          ensureLive();
          return entry.channel.presence.get();
        },
        onPresenceEvent: (
          cb: (
            members: Ably.PresenceMessage[],
            lastEvent: Ably.PresenceMessage,
          ) => void,
        ): (() => void) => {
          ensureLive();
          // INV-15 + INV-17: rebuild the coherent member set on every event,
          // including the synthetic 'present' event Ably fires after attach.
          const wrapper = async (msg: Ably.PresenceMessage): Promise<void> => {
            if (released) return;
            try {
              const members = await entry.channel.presence.get();
              if (released) return;
              cb(members, msg);
            } catch (err) {
              devWarn(
                `onPresenceEvent presence.get for "${entry.name}" failed:`,
                err,
              );
            }
          };
          void entry.channel.presence.subscribe(wrapper);
          const set =
            entry.presenceReadListeners.get(handleId) ??
            new Set<(msg: Ably.PresenceMessage) => void>();
          set.add(wrapper);
          entry.presenceReadListeners.set(handleId, set);
          if (entry.options?.attachOnSubscribe === false) {
            void this.#ensureAttached(entry);
          }
          return () => {
            entry.channel.presence.unsubscribe(wrapper);
            entry.presenceReadListeners.get(handleId)?.delete(wrapper);
          };
        },
      } satisfies Omit<
        PresenceReaderHandle<TPayload>,
        keyof MessageReaderHandle
      >,
    ) as PresenceReaderHandle<TPayload>;

    return presenceHandle;
  }

  // ------------------------- Pusher handle --------------------------------

  #buildPusherHandle<TPayload extends Record<string, unknown>>(
    entry: ChannelEntry,
    handleId: symbol,
    initialPayload: TPayload,
  ): PresencePusherHandle<TPayload> {
    const reader = this.#buildReaderHandle(entry, handleId);
    const presenceReader = this.#withPresenceReaderSurface<TPayload>(
      reader,
      entry,
      handleId,
    );
    let released = false;
    const ensureLive = (): void => {
      if (released) {
        throw new ChannelHandleReleasedError(entry.name, handleId);
      }
    };

    // Override release to add the presence-leave path (INV-3) before
    // refcount cleanup. The release() call is sync per the public type, so
    // we kick off the leave + slot-clear asynchronously and let the rest
    // of the cleanup proceed.
    const baseRelease = presenceReader.release.bind(presenceReader);
    const pusherRelease = (): void => {
      if (released) return;
      released = true;
      // INV-3: emit presence.leave bounded by 2s race, only if entered.
      // We do this BEFORE the refcount-driven detach so the leave goes out
      // while the channel is still attached.
      void this.#leaveAndClearPusherSlot(entry, handleId).finally(() => {
        baseRelease();
      });
    };

    const pusherHandle: PresencePusherHandle<TPayload> = Object.assign(
      presenceReader,
      {
        enter: async (payload: TPayload): Promise<void> => {
          ensureLive();
          await entry.channel.presence.enter(payload);
          entry.pusherHasEntered = true;
        },
        update: async (payload: TPayload): Promise<void> => {
          ensureLive();
          await entry.channel.presence.update(payload);
          // Per Ably docs: update before enter is treated as enter. The
          // server-side fact that something is now in the presence set
          // means we must emit leave on release.
          entry.pusherHasEntered = true;
        },
        leave: async (): Promise<void> => {
          ensureLive();
          if (!entry.pusherHasEntered) return;
          await entry.channel.presence.leave();
          entry.pusherHasEntered = false;
        },
        release: pusherRelease,
      } satisfies Omit<
        PresencePusherHandle<TPayload>,
        keyof PresenceReaderHandle<TPayload>
      > & { release: () => void },
    ) as PresencePusherHandle<TPayload>;

    // Stash the initial payload as the canonical source for the FIRST enter.
    // Consumers typically call handle.enter(initialPayload) themselves; we
    // do NOT auto-enter so the consumer remains in control of timing
    // (e.g. wait for full state hydration before announcing presence).
    void initialPayload; // referenced for type — auto-enter is opt-in

    return pusherHandle;
  }

  async #leaveAndClearPusherSlot(
    entry: ChannelEntry,
    handleId: symbol,
  ): Promise<void> {
    if (entry.pusherHandleId !== handleId) return;
    if (entry.pusherHasEntered) {
      // INV-3 / AC-PEL-2: bounded 2s race
      let timedOut = false;
      const timeout = new Promise<void>((resolve) => {
        setTimeout(() => {
          timedOut = true;
          resolve();
        }, 2000);
      });
      try {
        await Promise.race([
          entry.channel.presence.leave().catch(() => {
            /* connection gone — proceed with slot release */
          }),
          timeout,
        ]);
      } catch {
        /* swallow — the timeout always resolves */
      }
      if (timedOut) {
        const msg = `[AblyChannelRegistry] presence.leave timed out after 2s for "${entry.name}"; slot freed regardless.`;
        devWarn(msg);
        reportSentryBreadcrumb({
          category: "ably-registry",
          level: "warning",
          message: "presence.leave timeout",
          data: { channelName: entry.name, elapsedMs: 2000 },
        });
      }
    }
    entry.pusherHandleId = null;
    entry.pusherHasEntered = false;
  }

  // ------------------------- Release path ---------------------------------

  #releaseHandle(
    entry: ChannelEntry,
    handleId: symbol,
    isPusher: boolean,
  ): void {
    // 1. Unsubscribe message listeners for this handle (INV-1).
    const messageMap = entry.messageListeners.get(handleId);
    if (messageMap) {
      for (const [topic, wrapper] of messageMap) {
        entry.channel.unsubscribe(topic, wrapper);
      }
      entry.messageListeners.delete(handleId);
    }

    // 2. Unsubscribe presence-read listeners for this handle.
    const presenceSet = entry.presenceReadListeners.get(handleId);
    if (presenceSet) {
      for (const wrapper of presenceSet) {
        entry.channel.presence.unsubscribe(wrapper);
      }
      entry.presenceReadListeners.delete(handleId);
    }

    // 3. State-change listeners — registry-internal, no Ably-side cleanup.
    entry.stateChangeListeners.delete(handleId);
    entry.publishQueue.delete(handleId);

    // 4. Pusher-slot already cleared by leaveAndClearPusherSlot for pusher
    //    handles; this branch is a defensive no-op.
    if (isPusher && entry.pusherHandleId === handleId) {
      entry.pusherHandleId = null;
      entry.pusherHasEntered = false;
    }

    // 5. Refcount + last-handle cleanup (INV-2 / CRITICAL-B).
    entry.refCount -= 1;
    if (entry.refCount < 0) {
      entry.refCount = 0;
      console.error(
        "[ably-registry] refcount underflow on channel",
        entry.name,
      );
      reportSentryBreadcrumb({
        category: "ably-registry",
        level: "error",
        message: "refcount underflow",
        data: { channelName: entry.name },
      });
    }
    devLog(
      `release "${entry.name}" handle=${handleId.toString()} refCount=${entry.refCount}`,
    );

    if (entry.refCount === 0) {
      void this.#teardownEntry(entry);
    }
  }

  async #teardownEntry(entry: ChannelEntry): Promise<void> {
    if (entry.isReleasing) return;
    entry.isReleasing = true;
    const key = `${entry.name}#${entry.optionsHash}`;
    try {
      // INV-2: detach → whenState("detached") → channels.release.
      await entry.channel.detach().catch(() => {
        /* already detached / failed — proceed */
      });
      const state = entry.channel.state;
      if (
        state !== "detached" &&
        state !== "failed" &&
        state !== "initialized"
      ) {
        await entry.channel.whenState("detached").catch(() => {
          /* swallow */
        });
      }
      if (entry.internalStateHandler) {
        entry.channel.off(entry.internalStateHandler);
        entry.internalStateHandler = null;
      }
      // INV-2 / CRITICAL-B / AC-PEL-1: release the SDK-side channel object.
      this.#ablySdk.channels.release(entry.name);
    } finally {
      this.#entries.delete(key);
      this.#stats.delete(key);
      devLog(`entry torn down "${entry.name}"`);
    }
  }

  #refreshStats(): void {
    this.#stats.clear();
    for (const [key, entry] of this.#entries) {
      let listenerCount = 0;
      for (const m of entry.messageListeners.values()) listenerCount += m.size;
      for (const s of entry.presenceReadListeners.values())
        listenerCount += s.size;
      for (const s of entry.stateChangeListeners.values())
        listenerCount += s.size;
      const readerCount = Math.max(
        0,
        entry.refCount - (entry.pusherHandleId ? 1 : 0),
      );
      this.#stats.set(key, {
        name: entry.name,
        refCount: entry.refCount,
        readerCount,
        pusherHandleId: entry.pusherHandleId,
        pusherHasEntered: entry.pusherHasEntered,
        listenerCount,
        optionsHash: entry.optionsHash,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Test-only helpers — used by leak-invariants.test.ts and the mock registry.
  // These are NOT part of the AblyChannelRegistry public interface.
  // ---------------------------------------------------------------------------

  #resolveKey(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
  ): string {
    const name = descriptor.name(...channelArgs);
    const optionsHash = canonicalizeChannelOptions(descriptor.options);
    return `${name}#${optionsHash}`;
  }

  getRefCount(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
  ): number {
    return (
      this.#entries.get(this.#resolveKey(descriptor, channelArgs))?.refCount ??
      0
    );
  }

  getPusherHandleId(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
  ): symbol | null {
    return (
      this.#entries.get(this.#resolveKey(descriptor, channelArgs))
        ?.pusherHandleId ?? null
    );
  }

  emitMessage(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
    topic: string,
    msg: Ably.InboundMessage,
  ): void {
    const entry = this.#entries.get(this.#resolveKey(descriptor, channelArgs));
    if (!entry) return;
    const allListeners: Array<(msg: Ably.InboundMessage) => void> = [];
    for (const handleMap of entry.messageListeners.values()) {
      const wrapper = handleMap.get(topic);
      if (wrapper) allListeners.push(wrapper);
    }
    for (const fn of allListeners) fn(msg);
  }

  emitChannelStateChange(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
    change: Ably.ChannelStateChange,
  ): void {
    const entry = this.#entries.get(this.#resolveKey(descriptor, channelArgs));
    if (!entry?.internalStateHandler) return;
    entry.internalStateHandler(change);
  }

  emitConnectionState(_state: string): void {
    // The real impl reacts to Ably's own connection events. In tests, use
    // emitChannelStateChange to trigger channel-level state transitions.
  }

  emitPresenceEvent(
    descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    channelArgs: unknown[],
    msg: Ably.PresenceMessage,
  ): void {
    const entry = this.#entries.get(this.#resolveKey(descriptor, channelArgs));
    if (!entry) return;
    const allListeners: Array<(msg: Ably.PresenceMessage) => void> = [];
    for (const set of entry.presenceReadListeners.values()) {
      for (const fn of set) allListeners.push(fn);
    }
    for (const fn of allListeners) fn(msg);
  }

  setPresenceMembers(
    _descriptor: {
      name: (...args: any[]) => string;
      options: RegistryChannelOptions | undefined;
    },
    _channelArgs: unknown[],
    _members: Ably.PresenceMessage[],
  ): void {
    // On the real impl this is a no-op; presence is driven by the Ably SDK.
  }

  onHistoryCall(_cb: (args: unknown) => void): void {
    // On the real impl this is a no-op; the mock overrides this.
  }

  dispose(): void {
    // The registry doesn't own the Ably connection. Cleanup is handle-driven.
  }
}

// ---------------------------------------------------------------------------
// Provide / inject (Vue DI). Mirrors the use-ably-sdk module-level pattern.
// ---------------------------------------------------------------------------

export const ablyChannelRegistryInjectionKey = Symbol(
  "AblyChannelRegistry",
) as InjectionKey<AblyChannelRegistry>;

let __registryInstance: AblyChannelRegistry | null = null;

/**
 * Provide the registry to the app. Pass the return value of `provideAblySdk(app)`
 * as `ablySdkDefinition` — the registry needs the raw SDK reference.
 *
 * Calling pattern (main.ts):
 *   const ablySdkDef = provideAblySdk(app);
 *   provideAblyChannelRegistry(app, ablySdkDef);
 *
 * If `ablySdkDefinition` is omitted, the registry falls back to `useAblySdk()`,
 * which requires being called inside a component setup (e.g. App.vue).
 */
export function provideAblyChannelRegistry(
  app: App,
  ablySdkDefinition?: { sdk: Ably.Realtime },
): AblyChannelRegistry {
  if (__registryInstance !== null) {
    if (import.meta.env.DEV) {
      devWarn(
        "provideAblyChannelRegistry called twice; reusing existing instance (HMR-safe).",
      );
      app.provide(ablyChannelRegistryInjectionKey, __registryInstance);
      return __registryInstance;
    }
    throw new Error(
      "provideAblyChannelRegistry must be called only once per app instance.",
    );
  }

  const ablySdk = ablySdkDefinition ?? useAblySdk();
  __registryInstance = new AblyChannelRegistryImpl(ablySdk.sdk);
  app.provide(ablyChannelRegistryInjectionKey, __registryInstance);

  // Expose stats snapshot for DevTools (TICKET-025).
  if (import.meta.env.DEV) {
    (
      window as unknown as {
        __ablyRegistryStats?: () => unknown;
      }
    ).__ablyRegistryStats = () => {
      const obj: Record<string, unknown> = {};
      // Cast safe — instance is the impl we just constructed.
      const stats = __registryInstance!.stats;
      for (const [key, value] of stats) {
        obj[key] = {
          ...value,
          pusherHandleId: value.pusherHandleId
            ? value.pusherHandleId.toString()
            : null,
        };
      }
      return obj;
    };
  }

  return __registryInstance;
}

export function useAblyChannelRegistry(): AblyChannelRegistry {
  const reg = inject(ablyChannelRegistryInjectionKey);
  if (!reg) {
    throw new Error(
      "useAblyChannelRegistry must be used within a component that has been provided with the registry. Call provideAblyChannelRegistry(app) at app bootstrap.",
    );
  }
  return reg;
}

/**
 * Test-only escape hatch: reset the module-level singleton so a fresh
 * provideAblyChannelRegistry call works between tests. Prod build leaves
 * this exported but consumers shouldn't call it.
 */
export function __resetAblyChannelRegistryForTests(): void {
  __registryInstance = null;
}
